import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { ArrowRight, ShoppingBag, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatPrice, pluralize } from '@/lib/format'
import { Button, buttonVariants } from '@/components/ui/button'
import ProductImage from '@/components/shop/product-image'
import QuantityStepper from '@/components/shop/quantity-stepper'
import { useCart } from '@/components/shop/cart-provider'

import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle
} from '@/components/ui/sheet'

const CartDrawer: FC = (): ReactNode => {
    const router = useRouter()

    const {
        lines,
        itemCount,
        subtotal,
        isOpen,
        setOpen,
        setQuantity,
        removeItem
    } = useCart()

    useEffect(() => {
        const close = () => setOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events, setOpen])

    return (
        <Sheet open={isOpen} onOpenChange={open => setOpen(open)}>
            <SheetContent side='right' className='gap-0 p-0'>
                <SheetHeader className='border-b border-border pr-14'>
                    <SheetTitle>
                        Your cart
                    </SheetTitle>

                    <SheetDescription>
                        {itemCount > 0
                            ? `${itemCount} ${pluralize(itemCount, "item")} to reserve. You pay when you pick up.`
                            : "Reserve gear online and pay when you pick it up."}
                    </SheetDescription>
                </SheetHeader>

                {lines.length === 0 ? (
                    <div className='flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center'>
                        <span className='flex size-16 items-center justify-center rounded-full bg-secondary text-primary'>
                            <ShoppingBag className='size-7' strokeWidth={1.5} />
                        </span>

                        <div>
                            <p className='font-heading text-lg font-bold'>
                                Your cart is empty
                            </p>

                            <p className='mt-1 text-sm text-muted-foreground'>
                                Add a few things and we will have them ready at the counter.
                            </p>
                        </div>

                        <Link href='/shop' className={buttonVariants({ variant: 'accent' })}>
                            Browse the shop
                            <ArrowRight />
                        </Link>
                    </div>
                ) : (
                    <>
                        <ul className='flex-1 divide-y divide-border overflow-y-auto px-5'>
                            {lines.map(line => (
                                <li key={line.key} className='flex gap-4 py-4'>
                                    <Link
                                        href={`/shop/${line.slug}`}
                                        tabIndex={-1}
                                        aria-hidden='true'
                                        className='relative size-20 shrink-0 overflow-hidden rounded-2xl ring-1 ring-border'
                                    >
                                        <ProductImage
                                            src={line.image}
                                            alt={line.image_alt ?? line.name}
                                            categorySlug={line.category_slug}
                                            iconClassName='size-7'
                                        />
                                    </Link>

                                    <div className='flex min-w-0 flex-1 flex-col'>
                                        <div className='flex items-start justify-between gap-3'>
                                            <div className='min-w-0'>
                                                <Link
                                                    href={`/shop/${line.slug}`}
                                                    className='leading-snug font-semibold underline-offset-4 hover:underline'
                                                >
                                                    <span data-mdlfy-cms={`products:${line.product_id}:name`}>
                                                        {line.name}
                                                    </span>
                                                </Link>

                                                {line.variant && (
                                                    <p className='mt-0.5 text-xs text-muted-foreground'>
                                                        {line.variant}
                                                    </p>
                                                )}
                                            </div>

                                            <p className='shrink-0 font-semibold tabular-nums'>
                                                {formatPrice(line.price * line.quantity)}
                                            </p>
                                        </div>

                                        <div className='mt-auto flex items-center justify-between gap-2 pt-3'>
                                            <QuantityStepper
                                                size='sm'
                                                value={line.quantity}
                                                label={line.name}
                                                onChange={quantity => setQuantity(line.key, quantity)}
                                            />

                                            <Button
                                                variant='ghost'
                                                size='sm'
                                                onClick={() => removeItem(line.key)}
                                                className='text-muted-foreground'
                                            >
                                                <Trash2 />
                                                Remove
                                            </Button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        <SheetFooter className='gap-3 border-t border-border bg-muted/50'>
                            <div className='flex items-center justify-between text-base'>
                                <span className='font-medium'>
                                    Subtotal
                                </span>

                                <span className='font-heading text-xl font-extrabold tabular-nums'>
                                    {formatPrice(subtotal)}
                                </span>
                            </div>

                            <p className='text-xs text-muted-foreground'>
                                Nothing is charged online. Pay by card or cash at the counter.
                            </p>

                            <Link
                                href='/cart'
                                className={cn(buttonVariants({ variant: 'accent', size: 'lg' }), 'w-full')}
                            >
                                Reserve for pickup
                                <ArrowRight />
                            </Link>

                            <Button variant='ghost' onClick={() => setOpen(false)}>
                                Keep shopping
                            </Button>
                        </SheetFooter>
                    </>
                )}
            </SheetContent>
        </Sheet>
    )
}

export default CartDrawer
